/* eslint-disable react/jsx-no-undef */
// lingland-frontend/src/pages/AboutPage.js
/* eslint-disable no-unused-vars */
import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SEO from '../components/SEO';
import useIntersectionObserver from '../hooks/useIntersectionObserver';
import { Target, Zap, Eye, Cpu, Users, Award, Languages, HeartPulse, Scale, Megaphone, Factory, MonitorSmartphone, Briefcase, Gamepad2, Leaf, ShoppingCart } from 'lucide-react';
import teamIllustration from '../assets/images/team-illustration.png';
import './AboutPage.css';

const ClientDescriptionPlaceholder = ({ description }) => (
    <div className="client-description-placeholder">{description}</div>
);

function AboutPage() {
    const { t } = useTranslation();
    const [headerRef, isHeaderVisible] = useIntersectionObserver();
    const [missionRef, isMissionVisible] = useIntersectionObserver();
    const [valuesRef, isValuesVisible] = useIntersectionObserver();
    const [teamRef, isTeamVisible] = useIntersectionObserver();
    const [industriesRef, isIndustriesVisible] = useIntersectionObserver();
    const [trustedRef, isTrustedVisible] = useIntersectionObserver();
    const [ctaRef, isCtaVisible] = useIntersectionObserver();

    return (
        <>
            <SEO
                title={t('about_header_title')}
                description={t('about_header_subtitle')}
                url="/o-nas"
            />
            <section ref={headerRef} className={`page-header-section section-bg-white animate-on-scroll ${isHeaderVisible ? 'is-visible' : ''}`}>
                <div className="container text-center">
                    <h1>{t('about_header_title')}</h1>
                    <p className="subtitle">{t('about_header_subtitle')}</p>
                </div>
            </section>

            <section ref={missionRef} className={`mission-section section-bg-light-gray animate-on-scroll ${isMissionVisible ? 'is-visible' : ''}`}>
                <div className="container mission-container">
                    <div className="mission-text">
                        <h2>{t('about_mission_title')}</h2>
                        <p>{t('about_mission_p1')}</p>
                        <p>{t('about_mission_p2')}</p>
                    </div>
                    <div className="mission-visual">
                         <Target size={150} strokeWidth={1} color="var(--color-accent)" opacity={0.8}/>
                    </div>
                </div>
            </section>

            <section ref={valuesRef} className={`values-section section-bg-white animate-on-scroll ${isValuesVisible ? 'is-visible' : ''}`}>
                <div className="container">
                    <h2 className="text-center">{t('about_values_title')}</h2>
                    <div className="values-grid">
                        <div className="value-item">
                            <Zap size={48} strokeWidth={1.5} color="var(--color-accent)"/>
                            <h4>{t('about_value1_title')}</h4>
                            <p>{t('about_value1_desc')}</p>
                        </div>
                        <div className="value-item">
                            <Eye size={48} strokeWidth={1.5} color="var(--color-accent)"/>
                            <h4>{t('about_value2_title')}</h4>
                            <p>{t('about_value2_desc')}</p>
                        </div>
                        <div className="value-item">
                            <Cpu size={48} strokeWidth={1.5} color="var(--color-accent)"/>
                            <h4>{t('about_value3_title')}</h4>
                            <p>{t('about_value3_desc')}</p>
                        </div>
                         <div className="value-item">
                            <Users size={48} strokeWidth={1.5} color="var(--color-accent)"/>
                            <h4>{t('about_value4_title')}</h4>
                            <p>{t('about_value4_desc')}</p>
                        </div>
                    </div>
                </div>
            </section>

             <section ref={teamRef} className={`team-section section-bg-light-gray animate-on-scroll ${isTeamVisible ? 'is-visible' : ''}`}>
                <div className="container team-container">
                     <div className="team-visual">
                        <img src={teamIllustration} alt={t('about_team_title')} className="team-illustration"/>
                    </div>
                    <div className="team-text">
                        <h2>{t('about_team_title')}</h2>
                        <p>{t('about_team_p1')}</p>
                        <p>{t('about_team_p2')}</p>
                        <div className="team-highlights">
                           <div><Award size={24}/><span>{t('about_team_highlight1')}</span></div>
                           <div><Languages size={24}/><span>{t('about_team_highlight2')}</span></div>
                           <div><Briefcase size={24}/><span>{t('about_team_highlight3')}</span></div>
                        </div>
                    </div>
                </div>
            </section>

             <section ref={industriesRef} className={`industries-section section-bg-white animate-on-scroll ${isIndustriesVisible ? 'is-visible' : ''}`}>
                <div className="container text-center">
                    <h2>{t('about_industries_title')}</h2>
                     <p className="subtitle">{t('about_industries_subtitle')}</p>
                     <div className="industries-grid-about">
                        <div className="industry-item"><MonitorSmartphone size={32} strokeWidth={1.5}/><span>{t('industry_it')}</span></div>
                        <div className="industry-item"><Scale size={32} strokeWidth={1.5}/><span>{t('industry_legal')}</span></div>
                        <div className="industry-item"><HeartPulse size={32} strokeWidth={1.5}/><span>{t('industry_medical')}</span></div>
                        <div className="industry-item"><Megaphone size={32} strokeWidth={1.5}/><span>{t('industry_marketing')}</span></div>
                        <div className="industry-item"><ShoppingCart size={32} strokeWidth={1.5}/><span>{t('industry_ecommerce')}</span></div>
                        <div className="industry-item"><Factory size={32} strokeWidth={1.5}/><span>{t('industry_industrial')}</span></div>
                        <div className="industry-item"><Gamepad2 size={32} strokeWidth={1.5}/><span>{t('industry_gaming')}</span></div>
                        <div className="industry-item"><Leaf size={32} strokeWidth={1.5}/><span>{t('industry_ecology')}</span></div>
                    </div>
                </div>
            </section>

             <section ref={trustedRef} className={`trusted-by-section section-bg-light-gray animate-on-scroll ${isTrustedVisible ? 'is-visible' : ''}`}>
                 <div className="container text-center">
                     <h2>{t('about_trusted_title')}</h2>
                     <p className="subtitle">{t('about_trusted_subtitle')}</p>
                     <div className="clients-grid">
                         <ClientDescriptionPlaceholder description={t('client_placeholder1')}/>
                         <ClientDescriptionPlaceholder description={t('client_placeholder2')}/>
                         <ClientDescriptionPlaceholder description={t('client_placeholder3')}/>
                         <ClientDescriptionPlaceholder description={t('client_placeholder4')}/>
                         <ClientDescriptionPlaceholder description={t('client_placeholder5')}/>
                         <ClientDescriptionPlaceholder description={t('client_placeholder6')}/>
                     </div>
                 </div>
             </section>

             <section ref={ctaRef} className={`cta-about-end section-bg-white animate-on-scroll ${isCtaVisible ? 'is-visible' : ''}`}>
                 <div className="container text-center">
                     <h2>{t('about_cta_title')}</h2>
                     <p className="subtitle">{t('about_cta_subtitle')}</p>
                     <Link to="/wycena" className="button-outline-primary">
                         {t('about_cta_button')}
                     </Link>
                 </div>
             </section>
        </>
    );
}

export default AboutPage;